// ── SIGN-UP STEP 2 · THE MEMBER-TYPE TAXONOMY ────────────────────────────────────────────────────
// Card 86bb597gj (SIGNUP FUNNEL), founder ruling 2026-07-29.
//
// THIS FILE IS DATA, AND THAT IS THE POINT. The card's DoD says "build the step so the list is data,
// not markup" — because the list has already been replaced once (the 2023 Figma Step-2 board by the
// founder's own member-types slide) and will be edited again. Swapping the taxonomy must be an edit
// to this array and nothing else; no component may hard-code a label.
//
// PROVENANCE, so nobody has to guess which of the two lists is live:
//   • SUPERSEDED — FIGMA-REFS-S36/sign-in-up.png "Step 2 sign up" (2023). Two cards, Personal /
//     Business, plus an "I Am A Business Interested In" block. DESIGN-INVENTORY.md §1 records it.
//   • CURRENT — the founder's member-types slide, read at full resolution and transcribed into card
//     86bb597gj on 2026-07-29. THREE cards, each "Select All That Apply. You Are A:", each
//     independently checkable (one account can be all three).
// The card states the deltas explicitly: Travel Rep leaves Personal and Pro Or Athlete joins it;
// Business consolidates (Hotel/Lodging → Accomodations, Rentals + Retail → one row) and gains the
// brand/athlete/sponsor group BELOW A DIVIDER, replacing the old "I Am A Business Interested In"
// block; Ski Clubs is entirely new.
//
// ⚠ LABELS ARE VERBATIM FROM THE FOUNDER'S SLIDE, INCLUDING ITS SPELLING. "Accomodations",
// "Im In A Ski/Board Club" and "Travel Agency /Agent /Rep" are his, not typos of mine. Do NOT
// "correct" them here — the card says "Do not invent tier names beyond the Figma render; the founder
// image supersedes when it lands", and silently re-spelling a founder-supplied label is the same
// class of edit as inventing one. If they should read differently that is a one-word founder call.
//
// ⚠ NOT A TIER LIST. The card's MEMBERSHIP MODEL note is explicit: "Free Basic Profiles · Paid
// Tiered Options · +Add-Ons. Tier is an ORTHOGONAL axis to type — do NOT add tier checkboxes to
// sign-up." So this describes WHO THE PERSON IS, never what they pay.
//
// ⚠ AND IT IS NOT AN ACCOUNT-TYPE SWITCH. Founder architecture ruling of the same day (Facebook
// Pages model): the PERSONAL PROFILE is the only thing with a login. Business Pages and Club Pages
// are independent entities with no logins of their own, managed by people holding roles. So ticking
// "Accomodations" here does NOT create a business account — it records a fact about the person and
// may deep-link to "create a Page" AFTER sign-up completes. See `pageIntentsFor` below.

export type MemberTypeOption = {
  /** stable id, persisted — `${groupId}:${slug}`. Renaming a LABEL must not orphan stored data. */
  id: string;
  label: string;
};

export type MemberTypeGroup = {
  id: "personal" | "business" | "club";
  /** the card heading on the founder's slide */
  title: string;
  /** the slide's "Select if …" line, verbatim */
  blurb: string;
  /** the slide's shared instruction line */
  instruction: string;
  options: MemberTypeOption[];
  /**
   * Index into `options` BEFORE which a horizontal rule is drawn. The founder's Business card draws a
   * divider above the brand/athlete/sponsor group, which is the artefact of it having replaced the
   * 2023 "I Am A Business Interested In" block — so the divider carries meaning and is data, not a
   * styling flourish a component may decide on its own. `null` = no divider.
   */
  dividerBeforeIndex: number | null;
  /**
   * Which kind of PAGE this group's selections imply the person may want to create after sign-up.
   * `null` for personal: a personal profile is the login itself, not a Page.
   */
  pageKind: "business" | "club" | null;
};

const opt = (groupId: string, label: string): MemberTypeOption => ({
  id: `${groupId}:${label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")}`,
  label,
});

export const MEMBER_TYPE_GROUPS: MemberTypeGroup[] = [
  {
    id: "personal",
    title: "Personal",
    blurb: "Select if you buy/book for you and friends",
    instruction: "Select All That Apply. You Are A:",
    dividerBeforeIndex: null,
    pageKind: null,
    options: [
      "Skier",
      "Snowboarder",
      "Pro Or Athlete",
      "Student",
      "Edge Card Holder",
      "Season Pass Holder",
      "Im In A Ski/Board Club",
    ].map((l) => opt("personal", l)),
  },
  {
    id: "business",
    title: "Business",
    blurb:
      "Select if you or your team, or a business, use this account to connect with &/or sell to customers/fans",
    instruction: "Select All That Apply. You Are A:",
    // the divider sits above "Brand & Organizations", i.e. before index 6
    dividerBeforeIndex: 6,
    pageKind: "business",
    options: [
      "Accomodations",
      "Rental & Retail Store",
      "Activities Provider",
      "Transportation",
      "Food & Beverage",
      "Travel Agency /Agent /Rep",
      "Brand & Organizations",
      "Athlete & Public Figure",
      "Sponsor / Advertiser",
    ].map((l) => opt("business", l)),
  },
  {
    id: "club",
    title: "Ski Clubs",
    blurb: "Select if you or an existing club, want to create a new club page",
    instruction: "Select All That Apply. You Are A:",
    dividerBeforeIndex: null,
    pageKind: "club",
    // 2026-08-17 correction (95089480) against card 86bb597gj's transcription of the 2026-07-29 ruling:
    // "Ski Club · School · University & College · Race Team · Snow Sport Association". The earlier
    // "Association / Organization" and "Brand / Business" rows were never the founder's.
    options: [
      "Ski Club",
      "School",
      "University & College",
      "Race Team",
      "Snow Sport Association",
    ].map((l) => opt("club", l)),
  },
];

/** every valid id, for validating anything read back from storage or the wire */
export const MEMBER_TYPE_IDS: string[] = MEMBER_TYPE_GROUPS.flatMap((g) => g.options.map((o) => o.id));

export function isMemberTypeId(id: string): boolean {
  return MEMBER_TYPE_IDS.includes(id);
}

export function labelFor(id: string): string | null {
  for (const g of MEMBER_TYPE_GROUPS) {
    const hit = g.options.find((o) => o.id === id);
    if (hit) return hit.label;
  }
  return null;
}

/**
 * The POST-SIGN-UP Page deep-links implied by a selection set, per the founder's Pages ruling:
 * "'Create a business Page' / 'create a club Page' are post-signup actions … NOT sign-up branches."
 *
 * Returns the kinds in group order, deduped. An empty array is the normal case and means the
 * confirmation shows no Page offer at all — a person who ticked only Personal rows is not asked to
 * create anything.
 */
export function pageIntentsFor(selectedIds: string[]): Array<"business" | "club"> {
  const kinds: Array<"business" | "club"> = [];
  for (const g of MEMBER_TYPE_GROUPS) {
    if (!g.pageKind) continue;
    if (g.options.some((o) => selectedIds.includes(o.id)) && !kinds.includes(g.pageKind)) {
      kinds.push(g.pageKind);
    }
  }
  return kinds;
}
